import { useEffect, useRef, useState } from "react"

import {
  MathfieldElement,
  initVirtualKeyboardInCurrentBrowsingContext,
} from "mathlive"
import Button from "../../button/button"
import EditorOverlay from "./EditorOverlay"
import {
  CHEMISTRY_KEYBOARD_LAYOUTS,
  MATH_KEYBOARD_LAYOUTS,
  PHYSICS_KEYBOARD_LAYOUTS,
} from "../utils/chemistryKeyboardLayout"
import {
  parseChemistryLatex,
  toChemistryEditorValue,
  toStoredChemistryLatex,
} from "../utils/chemistryLatex"
import {
  getChemistryBodyBeforeCursor,
  getPlusInsertForChemistry,
  shouldUseIonChargePlus,
} from "../utils/chemistryKeystroke"
import "mathlive/static.css"
import "mathlive/fonts.css"
import "katex/dist/katex.min.css"

export type MathEditVariant = "math" | "physics" | "chemistry"

type MathEditModalProps = {
  isOpen: boolean
  title: string
  initialLatex?: string
  variant?: MathEditVariant
  onClose: () => void
  onSave: (latex: string) => void
}

const MATHLIVE_FONTS_CDN =
  "https://cdn.jsdelivr.net/npm/mathlive@0.110.0/fonts/"

const EQUATION_MODE_OPTIONS: Array<{
  value: MathEditVariant
  label: string
}> = [
  { value: "math", label: "Math" },
  { value: "physics", label: "Physics" },
  { value: "chemistry", label: "Chemistry" },
]

const keyboardLayoutsForVariant = (variant: MathEditVariant) => {
  switch (variant) {
    case "chemistry":
      return CHEMISTRY_KEYBOARD_LAYOUTS
    case "physics":
      return PHYSICS_KEYBOARD_LAYOUTS
    default:
      return MATH_KEYBOARD_LAYOUTS
  }
}

const descriptionForVariant = (variant: MathEditVariant) => {
  switch (variant) {
    case "chemistry":
      return ""
    case "physics":
      return "Type physics equations or use vectors, constants, and units."
    default:
      return "Type in the field below or use the symbol keyboard."
  }
}

const configureVirtualKeyboard = (
  container: HTMLElement,
  variant: MathEditVariant,
) => {
  const keyboard = window.mathVirtualKeyboard

  if (!keyboard) return

  keyboard.container = container
  keyboard.layouts = [...keyboardLayoutsForVariant(variant)]
}

const showVirtualKeyboard = (
  container: HTMLElement,
  variant: MathEditVariant,
) => {
  configureVirtualKeyboard(container, variant)

  requestAnimationFrame(() => {
    window.mathVirtualKeyboard?.show({ animate: true })
  })
}

const hideVirtualKeyboard = () => {
  window.mathVirtualKeyboard?.hide({ animate: true })
}

const resetKeyboardContainer = () => {
  const keyboard = window.mathVirtualKeyboard

  if (!keyboard) return

  keyboard.hide({ animate: false })
  keyboard.container = document.body
}

const configureMathfieldForVariant = (
  mathfield: MathfieldElement,
  variant: MathEditVariant
) => {
  if (variant === "chemistry") {
    // mhchem syntax is plain text; \\ce{} as a single atom blocks spaces and edits.
    mathfield.defaultMode = "text"
    mathfield.mode = "text"
    mathfield.mathModeSpace = " "
    return
  }

  mathfield.defaultMode = "math"
  mathfield.mode = "math"
  mathfield.mathModeSpace = ""
}

const MathEditModal = ({
  isOpen,
  title,
  initialLatex = "",
  variant = "math",
  onClose,
  onSave,
}: MathEditModalProps) => {
  const fieldHostRef = useRef<HTMLDivElement>(null)
  const keyboardHostRef = useRef<HTMLDivElement>(null)
  const mathfieldRef = useRef<MathfieldElement | null>(null)
  const activeVariantRef = useRef<MathEditVariant>(variant)
  const [activeVariant, setActiveVariant] = useState<MathEditVariant>(variant)
  const mathDraftRef = useRef("")
  const physicsDraftRef = useRef("")
  const chemistryDraftRef = useRef("")
  const chemistryCommandRef = useRef<"ce" | "pu">("ce")

  useEffect(() => {
    if (!isOpen) return

    mathDraftRef.current = variant === "math" ? initialLatex : ""
    physicsDraftRef.current = variant === "physics" ? initialLatex : ""
    if (variant === "chemistry") {
      const parsed = parseChemistryLatex(initialLatex)
      chemistryCommandRef.current = parsed.command === "pu" ? "pu" : "ce"
      chemistryDraftRef.current = toChemistryEditorValue(initialLatex)
    } else {
      chemistryDraftRef.current = ""
    }

    setActiveVariant(variant)
  }, [initialLatex, isOpen, variant])

  useEffect(() => {
    activeVariantRef.current = activeVariant
  }, [activeVariant])
  useEffect(() => {
    if (!isOpen) return

    document.body.classList.add("tiptap-math-modal-open")

    MathfieldElement.fontsDirectory = MATHLIVE_FONTS_CDN

    initVirtualKeyboardInCurrentBrowsingContext()

    const host = fieldHostRef.current
    const keyboardHost = keyboardHostRef.current

    if (!host || !keyboardHost) return

    configureVirtualKeyboard(keyboardHost, activeVariantRef.current)

    const mathfield = new MathfieldElement()

    mathfield.className = "tiptap-editor__mathfield"
    mathfield.mathVirtualKeyboardPolicy = "manual"
    mathfield.defaultMode = "math"
    mathfield.tabIndex = 0
    configureMathfieldForVariant(mathfield, activeVariantRef.current)
    mathfield.value =
      variant === "chemistry"
        ? chemistryDraftRef.current
        : variant === "physics"
          ? physicsDraftRef.current
          : mathDraftRef.current

    const readChemistryBodyBeforeCursor = () => {
      const pos = mathfield.position
      const before = mathfield.getValue(0, pos, "latex") ?? ""
      return getChemistryBodyBeforeCursor(before, mathfield.value)
    }

    const handleChemistryKeyDown = (event: KeyboardEvent) => {
      if (activeVariantRef.current !== "chemistry") return
      if (event.key !== "+" || event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      const bodyBefore = readChemistryBodyBeforeCursor()
      if (bodyBefore === null || shouldUseIonChargePlus(bodyBefore)) return

      event.preventDefault()
      event.stopImmediatePropagation()
      mathfield.insert(getPlusInsertForChemistry(bodyBefore))
    }

    const handleInput = () => {
      const nextValue = mathfield.value
      if (activeVariantRef.current === "chemistry") {
        chemistryDraftRef.current = nextValue
        return
      }

      if (activeVariantRef.current === "physics") {
        physicsDraftRef.current = nextValue
        return
      }

      mathDraftRef.current = nextValue
    }

    const handleActivate = () =>
      showVirtualKeyboard(keyboardHost, activeVariantRef.current)

    mathfield.addEventListener("focus", handleActivate)
    mathfield.addEventListener("pointerdown", handleActivate)
    mathfield.addEventListener("keydown", handleChemistryKeyDown, true)
    mathfield.addEventListener("input", handleInput)

    host.replaceChildren(mathfield)
    mathfieldRef.current = mathfield

    const focusTimer = window.setTimeout(() => {
      mathfield.focus()
      showVirtualKeyboard(keyboardHost, activeVariantRef.current)
    }, 200)

    return () => {
      window.clearTimeout(focusTimer)

      mathfield.removeEventListener("focus", handleActivate)
      mathfield.removeEventListener("pointerdown", handleActivate)
      mathfield.removeEventListener("keydown", handleChemistryKeyDown, true)
      mathfield.removeEventListener("input", handleInput)

      mathfield.blur()
      resetKeyboardContainer()
      host.replaceChildren()
      mathfieldRef.current = null
      document.body.classList.remove("tiptap-math-modal-open")
    }
  }, [initialLatex, isOpen, variant])

  useEffect(() => {
    const keyboardHost = keyboardHostRef.current
    const mathfield = mathfieldRef.current
    if (!keyboardHost || !mathfield) return

    configureVirtualKeyboard(keyboardHost, activeVariant)
    configureMathfieldForVariant(mathfield, activeVariant)
    if (activeVariant === "chemistry") {
      chemistryDraftRef.current = toChemistryEditorValue(chemistryDraftRef.current)
    }
    mathfield.value =
      activeVariant === "chemistry"
        ? chemistryDraftRef.current
        : activeVariant === "physics"
          ? physicsDraftRef.current
          : mathDraftRef.current
    mathfield.focus()
    showVirtualKeyboard(keyboardHost, activeVariant)
  }, [activeVariant])

  const handleSave = () => {
    const raw = (
      activeVariant === "chemistry"
        ? chemistryDraftRef.current
        : activeVariant === "physics"
          ? physicsDraftRef.current
          : mathDraftRef.current
    ).trim()
    if (!raw) return

    const latex =
      activeVariant === "chemistry"
        ? chemistryCommandRef.current === "pu"
          ? `\\pu{${raw}}`
          : toStoredChemistryLatex(raw)
        : raw
    if (!latex) return

    hideVirtualKeyboard()
    onSave(latex)
    onClose()
  }

  const handleClose = () => {
    hideVirtualKeyboard()
    onClose()
  }

  const variantDescription = descriptionForVariant(activeVariant)

  return (
    <EditorOverlay
      isOpen={isOpen}
      onClose={handleClose}
      className='tiptap-editor__math-modal'
    >
      <div className='tiptap-editor__math-modal-header'>
        <div>
          <h3>{title}</h3>
          {variantDescription ? <p>{variantDescription}</p> : null}
        </div>

        <div className='tiptap-editor__math-modal-actions'>
          <Button type='secondary' text='Cancel' onClick={handleClose} />
          <Button text='Insert' onClick={handleSave} />
        </div>
      </div>

      <div ref={fieldHostRef} className='tiptap-editor__mathfield-host' />

      <div
        className='tiptap-editor__math-keyboard-panel'
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div
          className='tiptap-editor__math-mode-bar'
          role='tablist'
          aria-label='Equation keyboard type'
        >
          {EQUATION_MODE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type='button'
              role='tab'
              aria-selected={activeVariant === option.value}
              className={`tiptap-editor__math-mode-button${
                activeVariant === option.value ? " is-active" : ""
              }`}
              onClick={() => setActiveVariant(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div
          ref={keyboardHostRef}
          className='tiptap-editor__math-keyboard-host'
        />
      </div>
    </EditorOverlay>
  )
}

export default MathEditModal
